import React, { useEffect, useState } from "react";
import { Lock, Check, Sparkles, CreditCard, Gift } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { useConfig } from "../lib/config";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function PremiumUnlockCard({ result, onUnlocked }) {
  const { t } = useI18n();
  const { config } = useConfig();
  const [loading, setLoading] = React.useState(false);
  const [pkg, setPkg] = useState(null);
  const isFree = config?.payment_mode === "free";

  // Live price from the admin-editable pricing_packages collection
  useEffect(() => {
    api
      .get("/pricing/packages")
      .then((res) => {
        const p = (res.data?.items || []).find((x) => x.id === "premium_unlock");
        setPkg(p || null);
      })
      .catch(() => {});
  }, []);

  const fmtPrice = () => {
    if (!pkg) return t("result.price"); // fallback to i18n string while loading
    const cur = (pkg.currency || "jpy").toUpperCase();
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: cur,
        maximumFractionDigits: 0,
      }).format(pkg.amount);
    } catch {
      return `${pkg.amount} ${cur}`;
    }
  };

  const features = [
    { key: "breakdown", label: t("result.feature_breakdown") },
    { key: "cause", label: t("result.feature_cause") },
    { key: "plan", label: t("result.feature_plan") },
    { key: "pdf", label: t("result.feature_pdf") },
  ];

  const handleAction = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout/session", {
        result_id: result.id,
        origin_url: window.location.origin,
        package_id: "premium_unlock",
      });
      if (data?.free && data?.redirect_url) {
        // Free mode — server already granted premium
        toast.success(t("payment.success"));
        if (typeof onUnlocked === "function") {
          // Refresh result in-place if parent wants to handle locally
          window.location.replace(data.redirect_url);
        } else {
          window.location.replace(data.redirect_url);
        }
        return;
      }
      if (!data?.url) throw new Error("No checkout URL");
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e));
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="premium-unlock-card"
      className="relative overflow-hidden border border-[#3B82F6] bg-[#0F1525] p-8 md:p-12 neon-glow-soft"
    >
      <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
      <div className="absolute top-6 right-6">
        <div className="inline-flex items-center gap-1.5 px-2 py-1 border border-[#3B82F6]/50 text-[#3B82F6] font-mono text-[10px] uppercase tracking-[0.2em]">
          {isFree ? (
            <>
              <Gift className="h-3 w-3" strokeWidth={1.5} />
              FREE · LIMITED TIME
            </>
          ) : (
            <>
              <Sparkles className="h-3 w-3" strokeWidth={1.5} />
              Premium
            </>
          )}
        </div>
      </div>

      <div className="relative flex items-center gap-3 mb-4">
        <div className="h-10 w-10 border border-[#3B82F6] text-[#3B82F6] flex items-center justify-center">
          <Lock className="h-5 w-5" strokeWidth={1.5} />
        </div>
        <h3 className="font-display text-3xl md:text-4xl tracking-tight uppercase text-white">
          {t("result.premium_title")}
        </h3>
      </div>

      <p className="relative text-sm text-[#94A3B8] mb-6 max-w-xl">{t("result.premium_subtitle")}</p>

      <ul className="relative grid md:grid-cols-2 gap-3 mb-8">
        {features.map((f) => (
          <li key={f.key} className="flex items-center gap-2 text-sm text-white">
            <span className="h-5 w-5 bg-[#3B82F6] text-black flex items-center justify-center">
              <Check className="h-3 w-3" strokeWidth={3} />
            </span>
            <span>{f.label}</span>
          </li>
        ))}
      </ul>

      <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-4 pt-6 border-t border-[#1E2A3F]">
        <div>
          <div className="font-mono text-4xl tracking-tight" style={{ color: isFree ? "#00FF94" : "#3B82F6" }}>
            {isFree ? t("result.price_free") : fmtPrice()}
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B] mt-1">
            {isFree ? t("result.price_free_note") : t("result.price_note")}
          </div>
        </div>
        <button
          onClick={handleAction}
          disabled={loading}
          data-testid="unlock-premium-button"
          className={
            "ml-auto inline-flex items-center gap-2 px-7 py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] disabled:opacity-60 pulse-glow " +
            (isFree
              ? "bg-[#00FF94] text-black hover:bg-[#22FFA0]"
              : "bg-[#3B82F6] text-black hover:bg-[#60A5FA]")
          }
        >
          {isFree ? <Gift className="h-3.5 w-3.5" strokeWidth={2} /> : <CreditCard className="h-3.5 w-3.5" strokeWidth={2} />}
          {loading
            ? t("result.unlocking")
            : isFree
            ? t("result.unlock_button_free")
            : t("result.unlock_button")}
        </button>
      </div>

      <p className="relative mt-4 font-mono text-[10px] text-[#64748B] leading-relaxed uppercase tracking-[0.1em]">
        {t("result.compliance_note")}
        {!isFree && <> · Stripe secure checkout</>}
      </p>
    </div>
  );
}
